// ─────────────────────────────────────────────────────────────────
// Glass-i — HR Employees helpers (HRMS-1 + HRMS-2). Pure JS — tested
// by the frontend node:test runner. Enum lists mirror the backend
// constants / migrations 0117 + 0120 (widened supersets).
// ─────────────────────────────────────────────────────────────────

export const EMPLOYEE_TYPE_OPTIONS = Object.freeze([
  { value: 'PERMANENT',  label: 'Permanent'  },
  { value: 'CONTRACT',   label: 'Contract'   },
  { value: 'TEMPORARY',  label: 'Temporary'  },
  { value: 'CONSULTANT', label: 'Consultant' },
  { value: 'TRAINEE',    label: 'Trainee'    },
  { value: 'APPRENTICE', label: 'Apprentice' },
  { value: 'INTERN',     label: 'Intern'     }
]);

export const ROLE_TYPE_OPTIONS = Object.freeze([
  { value: 'STAFF',   label: 'Staff'   },
  { value: 'WORKER',  label: 'Worker'  },
  { value: 'SALES',   label: 'Sales'   },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'ADMIN',   label: 'Admin'   }
]);

export const STATUS_OPTIONS = Object.freeze([
  { value: 'ACTIVE',        label: 'Active'        },
  { value: 'PROBATION',     label: 'Probation'     },
  { value: 'CONFIRMED',     label: 'Confirmed'     },
  { value: 'NOTICE_PERIOD', label: 'Notice Period' },
  { value: 'RELIEVED',      label: 'Relieved'      },
  { value: 'SUSPENDED',     label: 'Suspended'     },
  { value: 'INACTIVE',      label: 'Inactive'      },
  { value: 'LEFT',          label: 'Left (legacy)' }
]);

export const GENDER_OPTIONS = Object.freeze([
  { value: 'MALE', label: 'Male' }, { value: 'FEMALE', label: 'Female' }, { value: 'OTHER', label: 'Other' }
]);
export const MARITAL_OPTIONS = Object.freeze([
  { value: 'SINGLE', label: 'Single' }, { value: 'MARRIED', label: 'Married' },
  { value: 'DIVORCED', label: 'Divorced' }, { value: 'WIDOWED', label: 'Widowed' }, { value: 'OTHER', label: 'Other' }
]);

export const DOCUMENT_TYPE_OPTIONS = Object.freeze([
  'AADHAAR', 'PAN', 'DRIVING_LICENSE', 'PASSPORT', 'VOTER_ID',
  'OFFER_LETTER', 'JOINING_LETTER', 'ID_CARD', 'CERTIFICATE', 'OTHER'
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') })));
export const CONTACT_TYPE_OPTIONS = Object.freeze([
  'EMERGENCY', 'FAMILY', 'REFERENCE', 'NOMINEE', 'OTHER'
].map((v) => ({ value: v, label: v.charAt(0) + v.slice(1).toLowerCase() })));
export const ADDRESS_TYPE_OPTIONS = Object.freeze([
  { value: 'PERMANENT', label: 'Permanent' },
  { value: 'COMMUNICATION', label: 'Communication' },
  { value: 'OFFICE', label: 'Office' }
]);

// "Still employed" statuses — mirror backend ACTIVE_EMPLOYMENT_STATUSES.
export const ACTIVE_EMPLOYMENT_STATUSES = Object.freeze(['ACTIVE', 'PROBATION', 'CONFIRMED', 'NOTICE_PERIOD']);
export function isActiveEmploymentStatus(status) {
  return ACTIVE_EMPLOYMENT_STATUSES.includes(String(status || '').toUpperCase());
}

export function statusTagColor(status) {
  switch (String(status || '').toUpperCase()) {
    case 'ACTIVE': case 'CONFIRMED': return 'green';
    case 'PROBATION': return 'cyan';
    case 'NOTICE_PERIOD': return 'gold';
    case 'SUSPENDED': return 'orange';
    case 'RELIEVED': case 'LEFT': return 'volcano';
    case 'INACTIVE': return 'default';
    default: return 'default';
  }
}

const INDIAN_MOBILE = /^(?:\+?91|0)?[6-9]\d{9}$/;
export function isValidIndianMobile(v) {
  if (v === null || v === undefined || String(v).trim() === '') return true;
  return INDIAN_MOBILE.test(String(v).replace(/[\s-]/g, ''));
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isValidEmail(v) {
  if (v === null || v === undefined || String(v).trim() === '') return true;
  return EMAIL_RE.test(String(v).trim());
}

function trimOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}
// dayjs object (AntD DatePicker) or string → 'YYYY-MM-DD' or null.
function fmtDate(v) {
  if (!v) return null;
  if (typeof v === 'string') return v;
  return v.format ? v.format('YYYY-MM-DD') : null;
}

function buildSalespersonSettings(s) {
  if (!s || typeof s !== 'object') return undefined;
  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
  return {
    salespersonCode: trimOrNull(s.salespersonCode),
    territory: trimOrNull(s.territory),
    monthlyTarget: num(s.monthlyTarget),
    quarterlyTarget: num(s.quarterlyTarget),
    yearlyTarget: num(s.yearlyTarget),
    commissionEligible: s.commissionEligible === true,
    defaultCustomerLimit: num(s.defaultCustomerLimit),
    active: s.active !== false
  };
}

export function buildEmployeePayload(form) {
  if (!form || typeof form !== 'object') return {};
  const out = {
    employeeCode:  trimOrNull(form.employeeCode),
    employeeName:  trimOrNull(form.employeeName),
    displayName:   trimOrNull(form.displayName),
    mobile:        trimOrNull(form.mobile),
    email:         trimOrNull(form.email),
    departmentId:  form.departmentId || null,
    designationId: form.designationId || null,
    userId:        form.userId || null,
    employeeType:  form.employeeType || 'PERMANENT',
    roleType:      form.roleType || 'STAFF',
    isSalesperson: form.isSalesperson === true,
    joiningDate:   fmtDate(form.joiningDate),
    status:        form.status || 'ACTIVE',
    remarks:       trimOrNull(form.remarks),
    // ── HRMS-2 fields ──
    branchId:              form.branchId || null,
    reportsToEmployeeId:   form.reportsToEmployeeId || null,
    employeePhoto:         trimOrNull(form.employeePhoto),
    gender:                form.gender || null,
    dateOfBirth:           fmtDate(form.dateOfBirth),
    bloodGroup:            trimOrNull(form.bloodGroup),
    maritalStatus:         form.maritalStatus || null,
    confirmationDate:      fmtDate(form.confirmationDate),
    relievingDate:         fmtDate(form.relievingDate),
    probationMonths:       form.probationMonths === '' || form.probationMonths == null ? null : Number(form.probationMonths),
    officialMobile:        trimOrNull(form.officialMobile),
    officialEmail:         trimOrNull(form.officialEmail),
    emergencyContactName:  trimOrNull(form.emergencyContactName),
    emergencyContactMobile: trimOrNull(form.emergencyContactMobile),
    aadhaarLast4:          trimOrNull(form.aadhaarLast4),
    panLast4:              trimOrNull(form.panLast4),
    canLogin:              form.canLogin === true,
    loginEnabled:          form.loginEnabled === true,
    loginLocked:           form.loginLocked === true,
    designationEffectiveFrom: fmtDate(form.designationEffectiveFrom),
    departmentEffectiveFrom:  fmtDate(form.departmentEffectiveFrom),
    statusReason:          trimOrNull(form.statusReason)
  };
  const sp = buildSalespersonSettings(form.salespersonSettings);
  if (sp) out.salespersonSettings = sp;
  return out;
}

export function validateEmployeeForm(form) {
  const errors = [];
  if (!trimOrNull(form && form.employeeCode)) errors.push({ field: 'employeeCode', message: 'Employee Code is required.' });
  if (!trimOrNull(form && form.employeeName)) errors.push({ field: 'employeeName', message: 'Employee Name is required.' });
  if (form && form.mobile && !isValidIndianMobile(form.mobile)) errors.push({ field: 'mobile', message: 'Enter a valid Indian mobile number (10 digits starting 6-9).' });
  if (form && form.email && !isValidEmail(form.email)) errors.push({ field: 'email', message: 'Enter a valid email address.' });
  return { ok: errors.length === 0, errors };
}
